import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import KonfhubContainer from "../../../../revampComponents/Atom/Container";
import LoadingSpinner from "../../../../revampComponents/Atom/Loading";
import BackendPaginatedDataTable from "../../../../revampComponents/Atom/BackendPaginatedDataTable";
import TippyIcon from "../../../../revampComponents/Atom/TippyIcon";
import redirectIcon from "../../../../assets/img/reports/open-in-new-tab.svg";
import { dashboardColors, theme } from "../../../../themes/Theme";
import { baseUrl } from "../../../../config/config";
import { EVENT_STATUS, formatCurrency, formatEventDateTime } from "../AccountLevelReportConstant";
import { cutString } from "../../../../services/util";
import { getISTDateNTime } from "../../../../Utils/helpers";

const EventTable = ({
  events = [],
  totalCount = 0, // Total count from API
  onEventClick,
  loading = false,
  eventStatus,
  dateFilter,
  onPaginationChange ,
  selectedCurrency= null
}) => {
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const { eventId } = useParams();



  const formatStatus = (status) => {
    if (status === EVENT_STATUS.ACTIVE) return "Active";
    if (status === EVENT_STATUS.UPCOMING) return "Upcoming";
    return "Past";
  };

  const columns = [
    {
      name: "Event",
      selector: "name",
      
      minWidth: "360px",
      cell: (_data) => (
        <div className="d-flex align-items-center w-100 richtext-gap-2 p-2">
          <div className="event-image-container me-3">
            <img
              src={_data?.event_poster_url || '/default-event-poster.jpg'}
              alt={_data?.name}
              className="report-table-event-image"
              onError={(e) => {
                e.target.src = '/default-event-poster.jpg';
              }}
            />
          </div>
          <div className="event-details-container ml-1">
            <div className="report-table-event-name">{cutString(_data?.name, 40)}</div>
            <div className="report-table-event-status">
              <span className={`report-table-status-badge ${formatStatus(_data?.event_status).toLowerCase()}`}>
                <span
                  className={`report-table-dashboard-dot ${_data?.event_status === EVENT_STATUS.ACTIVE
                      ? 'dashboard-active-dot'
                      : _data?.event_status === EVENT_STATUS.UPCOMING
                        ? 'dashboard-upcoming-dot'
                        : 'dashboard-past-dot'
                    }`}
                ></span>
                <span className="dashboard-active">{formatStatus(_data?.event_status)}</span>
              </span>
              <span className="event-summary-event-start-time ms-2">
                {getISTDateNTime(_data?.start_date + " " + _data?.start_time, _data?.time_zone, "needed")} 
                
              </span>
            </div>
          </div>
        </div>
      ),
    },
    {
      name: "Registrations",
      selector: "registrations_count",
      
      minWidth: "120px",
      center: true,
      cell: (_data) => (
        <span className="registration-count">{_data?.registrations_count || 0}</span>
      ),
    },
    {
      name: "Sales",
      selector: "sale",
      
      minWidth: "120px",
     
      cell: (_data) => (
        <span className="event-summary-amount">
          {formatCurrency(_data?.sale,selectedCurrency?.symbol)}
        </span>
      ),
    },
    {
      name: "Refunds",
      selector: "refunds",
      
      minWidth: "120px",
     
      cell: (_data) => (
        <span className="event-summary-amount">
          {formatCurrency(_data?.refunds || 0, selectedCurrency?.symbol)}
        </span>
      ),
    },
    {
      name: "Expenses",
      selector: "expense",
      
      minWidth: "120px",
     
      cell: (_data) => (
        <span className="event-summary-amount">
          {formatCurrency(_data?.expense,selectedCurrency?.symbol)}
        </span>
      ),
    },
    {
      name: "Income",
      selector: "income",
      
      minWidth: "120px",
     
      cell: (_data) => (
        <span className="event-summary-amount income-positive">
          {formatCurrency(_data?.income,selectedCurrency?.symbol)}
        </span>
      ),
    },
    {
      name: "",
      selector: "actions",
      sortable: false,
      minWidth: "60px",
      center: true,
      cell: (_data) => (
        <TippyIcon
          content={<span>Go to Event Dashboard</span>}
        >
          <button
            className="report-table-action-button"
            onClick={(e) => {
              e.stopPropagation();
              onEventClick && onEventClick(_data?.event_id);
            }}
          >
            <img src={redirectIcon} alt="Go to Event Dashboard" />
          </button>
        </TippyIcon>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  // Handle pagination change - this triggers API call
  const handlePageChange = (page) => {
    const newOffset = (page - 1) * limit;
    setOffset(newOffset);

    if (onPaginationChange) {
      onPaginationChange({
        limit: limit,
        offset: newOffset,
        search: search,
        eventStatus: eventStatus,
        dateFilter: dateFilter
      });
    }
  };

  // Handle per page change - this triggers API call
  const handlePerRowsChange = (newPerPage, page) => {
    const newLimit = newPerPage;
    const newOffset = (page - 1) * newPerPage;

    setLimit(newLimit);
    setOffset(newOffset);

    if (onPaginationChange) {
      onPaginationChange({
        limit: newLimit,
        offset: newOffset,
        search: search,
        eventStatus: eventStatus,
        dateFilter: dateFilter
      });
    }
  };


  const onRowClicked = (row) => {
    if (onEventClick) {
      onEventClick(row.event_id);
    }
  };

  return (
    <KonfhubContainer className="event-table-parent-container">
      <div className="event-table">
        <BackendPaginatedDataTable
          rowData={events}
          columns={columns}
          totalCount={totalCount}
          paginationServer={true}
          onProgress={loading}
          pagination={true}
          noDataComponent={
            <div className="no-events-found">
              <p>No Events Found</p>
            </div>
          }
          handlePerRowsChange={handlePerRowsChange}
          handlePageChange={handlePageChange}
          onRowClicked={onRowClicked}
        />
      </div>

      <style jsx>
        {`
          .event-table-parent-container {
            padding: 20px;
          }

          .event-image-container {
            width: 76px;
            height: 38px;
            border-radius: 6px;
            overflow: hidden;
            flex-shrink: 0;
          }

          .report-table-event-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 6px;
          }

          .event-details-container {
            flex: 1;
            min-width: 0;
          }

          .report-table-dashboard-dot {
            width: 8px;
            height: 8px;
            background: #fdb631;
            border-radius: 50%;
            display: inline-block;
            margin-right: 4px;
          }

          .report-table-event-name {
            font-weight: 500;
            font-size: 14px;
            color: #000;
            margin-bottom: 4px;
            text-align:left;
          }

          .report-table-event-status {
            display: flex;
            align-items: center;
            font-size: 12px;
            gap: 5px;
            flex-wrap: wrap;
          }

          .report-table-status-badge {
            font-weight: 400;
            font-size: 12px;
            color: #000000;
            display: flex;
            align-items: center;
            gap: 4px;
          }

          .report-table-status-badge.active {
            color: #28a745;
          }

          .report-table-status-badge.past {
            color: #6c757d;
          }

          .event-summary-event-date {
            font-weight: 400;
            font-size: 12px;
            color: #6c757d;
          }

          .registration-count {
            font-weight: 500;
            font-size: 14px;
            color: #333333;
          }

          .event-summary-amount {
            font-weight: 500;
            font-size: 14px;
            color: ${dashboardColors[theme].primaryTextColor};
          }

          .income-positive {
            color: #34A853 !important;
            font-weight: 600;
          }

          .report-table-action-button {
            background: transparent;
            border: none;
            padding: 8px;
            border-radius: 4px;
            cursor: pointer;
            transition: background-color 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .event-summary-event-start-time {
              font-size: 12px;
              font-weight: 400;
              color: #000000;
              margin-left: 8px;
          }
        .dashboard-upcoming-dot{
            background-color:#FDB631;
            width: 8px;
            height: 8px;
          }
          .dashboard-active {
            color: #000000;
            font-weight: 400;
            font-size: 12px;
        }

          .report-table-action-button:hover {
            background-color: #f8f9fa;
          }

          .no-events-found {
            text-align: center;
            padding: 60px 20px;
            color: #6c757d;
          }

          .no-events-found p {
            font-weight: 500;
            font-size: 16px;
            margin: 0;
          }
         .dashboard-past-dot{
            background: #DC3545;
        }
        .dashboard-active-dot{
         background: #FDB631;
           
        }
          @media (max-width: 768px) {
            .event-image-container {
              width: 48px;
              height: 24px;
            }

            .report-table-event-name {
              font-size: 13px;
            }

            .report-table-event-status {
              font-size: 11px;
            }
          }
        `}
      </style>
    </KonfhubContainer>
  );
};

export default EventTable;
